import { DropOverlay } from "@/components/common/DropOverlay/DropOverlay"
import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { FileUploadIllustration } from "@/components/common/FileUploadIllustration/FileUploadIllustration"
import { toast } from "@/components/common/Toast"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import { IconLoader2 } from "@tabler/icons-react"
import { FolderOpen } from "lucide-react"
import { useRef } from "react"
import { useNavigate } from "react-router"
import { CUSTOMER_REQUESTS_BASE } from "../routes"
import {
  CUSTOMER_REQUEST_ACCEPTED_TYPES,
  CUSTOMER_REQUEST_DROP_EXTENSIONS,
  useCustomerRequestUpload,
} from "../useCustomerRequestUpload"

// FileUploadIllustration requires exactly two format badges.
const Q_FORMATS: [
  { label: string; colorClass: string },
  { label: string; colorClass: string },
] = [
  { label: "XLSX", colorClass: "text-success" },
  { label: "PDF", colorClass: "text-[#E06C75]" },
]

/**
 * Step 1 — upload the customer request. Calls the upload API (which runs AI
 * detection); the same drop zone stays on screen the whole time (dimmed +
 * disabled while pending), matching /questionnaire/upload's pattern rather
 * than swapping to a separate processing screen. On success it routes to the
 * confirm page with the returned questionnaire id.
 */
export function CustomerRequestImportPage() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { uploadFile: handleFile, isPending } = useCustomerRequestUpload()
  // Whole page is a write action; if the user can't upload (viewer-only who
  // typed the URL) the primary action just isn't rendered. Route gate on
  // MC_QUESTIONNAIRES_VIEW still lets them reach the page, so we hide inside.
  const { hasPermission } = useUser()
  const canUpload = hasPermission(PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)

  return (
    <div data-agent-target="outreach.request-import" className="h-full">
      <EmptyState
        illustration={<FileUploadIllustration formats={Q_FORMATS} />}
        title={translate("MC_Q_UPLOAD_CARD_TITLE")}
        description={
          <>
            {translate("MC_Q_UPLOAD_CARD_SUBTITLE")}
            <span className="mt-2 block text-grey5">
              {translate("MC_IMPORT_ELSEWHERE_MD")}{" "}
              <button
                type="button"
                className="font-medium text-yellow1 hover:underline"
                onClick={() =>
                  navigate("/material-compliance/master-data/import")
                }
              >
                {translate("MC_IMPORT_ELSEWHERE_MD_LINK")}
              </button>
            </span>
          </>
        }
        hoverable={!isPending}
        className={cn("h-full", isPending && "pointer-events-none opacity-60")}
        onClose={() => navigate(CUSTOMER_REQUESTS_BASE)}
        primaryAction={
          canUpload
            ? {
                label: isPending
                  ? translate("MC_Q_UPLOAD_PROCESSING")
                  : translate("MC_Q_UPLOAD_BTN_CHOOSE"),
                icon: isPending ? (
                  <IconLoader2 size={14} className="animate-spin" />
                ) : (
                  <FolderOpen size={14} />
                ),
                onClick: () => inputRef.current?.click(),
                disabled: isPending,
              }
            : undefined
        }
      />
      <input
        ref={inputRef}
        type="file"
        accept={CUSTOMER_REQUEST_ACCEPTED_TYPES}
        className="hidden"
        disabled={isPending}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void handleFile(f)
          e.target.value = ""
        }}
      />
      {!isPending && (
        <DropOverlay
          acceptedExtensions={CUSTOMER_REQUEST_DROP_EXTENSIONS}
          hint={translate("MC_Q_DROP_HINT")}
          onFilesDropped={(files) => void handleFile(files[0])}
          onRejectedFiles={() =>
            toast.error(translate("MC_Q_DROP_UNSUPPORTED"))
          }
        />
      )}
    </div>
  )
}
